/* SnappySteps — behaviour for sp-* sections (ES module, no dependencies).
   Integrates with Horizon's cart drawer through @shopify/events when available,
   and falls back to the cart page on themes without it. */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function formatMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const value = Number(cents || 0) / 100;
  const num = (decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = value.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  const map = {
    amount: () => num(2),
    amount_no_decimals: () => num(0),
    amount_with_comma_separator: () => num(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => num(0, '.', ','),
    amount_with_apostrophe_separator: () => num(2, "'", '.'),
    amount_with_space_separator: () => num(2, ' ', ','),
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (map[key] || map.amount)());
}

/* ------------------------------------------------------------------ */
/* Size picker: replaces the native <select> list with a branded panel   */
/* (the native select stays in the DOM as the source of truth)          */
/* ------------------------------------------------------------------ */
let spDdCount = 0;
function enhanceSelect(select) {
  if (!select || select._spDd) return select && select._spDd;
  const host = select.parentElement;
  const id = select.id || `sp-dd-${++spDdCount}`;
  select.id = id;
  const label = select.labels && select.labels[0];
  if (label && !label.id) label.id = `${id}-label`;

  const wrap = document.createElement('div');
  wrap.className = 'sp-dd';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sp-dd__btn';
  btn.id = `${id}-btn`;
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-controls', `${id}-list`);
  if (label) btn.setAttribute('aria-labelledby', `${label.id} ${btn.id}`);
  const value = document.createElement('span');
  value.className = 'sp-dd__value';
  const chev = document.createElement('span');
  chev.className = 'sp-dd__chev';
  chev.setAttribute('aria-hidden', 'true');
  btn.append(value, chev);

  const list = document.createElement('div');
  list.className = 'sp-dd__list';
  list.id = `${id}-list`;
  list.setAttribute('role', 'listbox');
  if (label) list.setAttribute('aria-labelledby', label.id);
  list.hidden = true;

  wrap.append(btn, list);
  host.insertBefore(wrap, select.nextSibling);
  host.classList.add('is-enhanced');
  select.classList.add('sp-native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');

  const clean = (t) => String(t || '').replace(/\s*\(sold out\)\s*$/i, '').trim();
  const isSoldOut = (opt) => opt.disabled || /\(sold out\)\s*$/i.test(opt.textContent);
  const options = () => [...list.querySelectorAll('[role="option"]')];

  function build() {
    list.replaceChildren();
    // Numeric values (shoe sizes) always show small → large, whatever order the product data has.
    const opts = [...select.options];
    const num = (o) => parseFloat(clean(o.dataset.label || o.textContent));
    if (opts.length && opts.every((o) => !Number.isNaN(num(o)))) opts.sort((a, b) => num(a) - num(b));
    opts.forEach((opt) => {
      const o = document.createElement('div');
      o.className = 'sp-dd__opt';
      o.setAttribute('role', 'option');
      o.tabIndex = -1;
      o.dataset.value = opt.value;
      o.id = `${id}-opt-${opt.index}`;
      const sold = isSoldOut(opt);
      const text = document.createElement('span');
      text.textContent = clean(opt.dataset.label || opt.textContent);
      o.append(text);
      if (sold) {
        o.setAttribute('aria-disabled', 'true');
        const small = document.createElement('small');
        small.textContent = 'Sold out';
        o.append(small);
      }
      o.setAttribute('aria-selected', String(opt.value === select.value));
      list.append(o);
    });
    const cur = select.options[select.selectedIndex];
    value.textContent = cur ? clean(cur.dataset.label || cur.textContent) : '';
  }

  function open() {
    build();
    list.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    const sel = list.querySelector('[aria-selected="true"]') || options()[0];
    if (sel) sel.focus();
  }
  function close(focusBtn) {
    if (list.hidden) return;
    list.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    if (focusBtn) btn.focus();
  }
  function choose(o) {
    if (!o || o.getAttribute('aria-disabled') === 'true') return;
    if (select.value !== o.dataset.value) {
      select.value = o.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    build();
    close(true);
  }

  btn.addEventListener('click', () => (list.hidden ? open() : close(true)));
  btn.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); open(); }
  });
  list.addEventListener('click', (e) => choose(e.target.closest('[role="option"]')));
  list.addEventListener('keydown', (e) => {
    const all = options();
    const i = all.indexOf(document.activeElement);
    const cols = 4;
    const move = (n) => { const t = all[Math.max(0, Math.min(all.length - 1, n))]; if (t) t.focus(); };
    if (e.key === 'ArrowRight') { e.preventDefault(); move(i + 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); move(i - 1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); move(i + cols); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(i - cols); }
    else if (e.key === 'Home') { e.preventDefault(); move(0); }
    else if (e.key === 'End') { e.preventDefault(); move(all.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(document.activeElement.closest('[role="option"]')); }
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') { close(false); }
  });
  document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) close(false); });

  build();
  select._spDd = { sync: build, close };
  return select._spDd;
}

/* ------------------------------------------------------------------ */
/* Product                                                             */
/* ------------------------------------------------------------------ */
class SpProduct extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    const json = this.querySelector('[data-sp-product]');
    if (!json) return;
    this.data = JSON.parse(json.textContent);
    this.form = this.querySelector('form[data-sp-form]');
    this.idInput = this.querySelector('[data-sp-variant-id]');
    this.addBtn = this.querySelector('[data-sp-add]');
    this.addLabel = this.querySelector('[data-sp-add-label]');
    this.status = this.querySelector('[data-sp-status]');
    this.mixWrap = this.querySelector('[data-sp-mix]');
    this.mixToggle = this.querySelector('[data-sp-mix-toggle]');
    this.mixToggleWrap = this.querySelector('[data-sp-mix-toggle-wrap]');
    this.qtyInput = this.querySelector('[data-sp-qty]');
    this.sticky = this.querySelector('[data-sp-sticky]');
    this.mixValues = [];

    this.addEventListener('change', (e) => this.onChange(e));
    this.addEventListener('click', (e) => this.onClick(e));
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));

    this.querySelectorAll('select[data-sp-option]').forEach((sel) => enhanceSelect(sel));
    this.update(false);
    this.initGallery();
    this.initSticky();
  }

  disconnectedCallback() {
    this._observers?.forEach((o) => o.disconnect());
  }

  /* ---------- state ---------- */
  selectedOptions() {
    if (!this.initialVariant) {
      const startId = Number(this.idInput?.value);
      this.initialVariant = this.data.variants.find((v) => v.id === startId) || this.data.variants[0];
    }
    const count = this.data.options.length;
    const values = [];
    for (let i = 0; i < count; i++) {
      const fixed = this.querySelector(`[data-sp-option="${i}"][data-sp-fixed]`);
      const checked = this.querySelector(`input[data-sp-option="${i}"]:checked, select[data-sp-option="${i}"]`);
      values.push((checked || fixed)?.value ?? this.initialVariant?.options[i] ?? null);
    }
    return values;
  }

  findVariant(options) {
    return this.data.variants.find((v) => v.options.every((o, i) => o === options[i])) || null;
  }

  tier() {
    return this.querySelector('input[data-sp-tier]:checked');
  }

  quantity() {
    const t = this.tier();
    if (t) return Number(t.value) || 1;
    if (this.qtyInput) return Math.min(10, Math.max(1, Number(this.qtyInput.value) || 1));
    return 1;
  }

  savings() {
    const t = this.tier();
    return t ? Number(t.dataset.savings) || 0 : 0;
  }

  total(price, qty, savings) {
    return Math.max(0, price * qty - savings);
  }

  unitWord(qty) {
    return qty === 1 ? this.data.unit : this.data.unitPlural;
  }

  mixActive() {
    return !!(this.mixToggle && this.mixToggle.checked && this.quantity() > 1 && this.data.mixOptionIndex > -1);
  }

  /* ---------- events ---------- */
  onChange(e) {
    const t = e.target;
    if (t.matches('[data-sp-option]')) {
      const label = this.querySelector(`[data-sp-option-label="${t.dataset.spOption}"]`);
      if (label) label.textContent = t.value;
      this.update(true);
    } else if (t.matches('[data-sp-tier], [data-sp-qty], [data-sp-mix-toggle]')) {
      this.update(false);
    } else if (t.matches('[data-sp-mix-select]')) {
      this.mixValues[Number(t.dataset.spMixSelect)] = t.value;
      this.update(false);
    }
  }

  onClick(e) {
    const step = e.target.closest('[data-sp-qty-step]');
    if (step && this.qtyInput) {
      const next = Math.min(10, Math.max(1, (Number(this.qtyInput.value) || 1) + Number(step.dataset.spQtyStep)));
      this.qtyInput.value = next;
      this.update(false);
      return;
    }
    const thumb = e.target.closest('[data-sp-thumb]');
    if (thumb) {
      this.goToMedia(thumb.dataset.spThumb, true);
      return;
    }
    if (e.target.closest('[data-sp-sticky-btn]')) {
      if (this.variant && this.variant.available) {
        this.form.requestSubmit(this.addBtn);
      } else {
        this.querySelector('.sp-buy')?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  /* ---------- render ---------- */
  update(fromUser) {
    const options = this.selectedOptions();
    const v = this.findVariant(options);
    this.variant = v;
    const fmt = this.data.moneyFormat;
    const qty = this.quantity();

    if (this.idInput) this.idInput.value = v ? v.id : '';

    // Price block
    if (v) {
      const priceEl = this.querySelector('[data-sp-price]');
      if (priceEl) priceEl.textContent = formatMoney(v.price, fmt);
      const cmp = this.querySelector('[data-sp-compare]');
      if (cmp) {
        const show = v.compare_at_price && v.compare_at_price > v.price;
        cmp.hidden = !show;
        const cv = cmp.querySelector('[data-sp-compare-value]');
        if (show && cv) cv.textContent = formatMoney(v.compare_at_price, fmt);
      }
      // Tier totals follow the selected variant's price
      this.querySelectorAll('input[data-sp-tier]').forEach((input) => {
        const label = input.closest('.sp-tier');
        const q = Number(input.value) || 1;
        const t = this.total(v.price, q, Number(input.dataset.savings) || 0);
        const totalEl = label.querySelector('[data-sp-tier-total]');
        const unitEl = label.querySelector('[data-sp-tier-unit]');
        if (totalEl) totalEl.textContent = formatMoney(t, fmt);
        if (unitEl) unitEl.textContent = formatMoney(Math.floor(t / q), fmt);
      });
    }

    // Option availability (still selectable, just marked)
    this.querySelectorAll('input[type="radio"][data-sp-option]').forEach((input) => {
      const idx = Number(input.dataset.spOption);
      const probe = options.slice();
      probe[idx] = input.value;
      const match = this.findVariant(probe);
      if (match && match.available) input.removeAttribute('data-unavailable');
      else input.setAttribute('data-unavailable', '');
    });

    // Dropdown options: keep every size selectable, label the sold-out ones
    this.querySelectorAll('select[data-sp-option]').forEach((select) => {
      const idx = Number(select.dataset.spOption);
      [...select.options].forEach((opt) => {
        if (opt.dataset.label === undefined) opt.dataset.label = opt.textContent.trim();
        const probe = options.slice();
        probe[idx] = opt.value;
        const match = this.findVariant(probe);
        opt.textContent = match && match.available ? opt.dataset.label : `${opt.dataset.label} (sold out)`;
      });
    });
    this.querySelectorAll('select[data-sp-option]').forEach((sel) => sel._spDd && sel._spDd.sync());

    // Mixed sizes
    this.renderMix(qty, options);

    // Button
    const total = v ? this.total(v.price, qty, this.savings()) : 0;
    let ok = !!(v && v.available);
    if (this.mixActive()) ok = ok && this.mixItems(options) !== null;
    if (this.addBtn) this.addBtn.disabled = !ok;
    if (this.addLabel) {
      if (!v) this.addLabel.textContent = this.data.unavailableLabel;
      else if (!v.available) this.addLabel.textContent = this.data.soldOutLabel;
      else this.addLabel.textContent = `${this.data.buttonLabel} · ${formatMoney(total, fmt)}`;
    }
    const dyn = this.querySelector('[data-sp-dynamic]');
    if (dyn) dyn.hidden = this.mixActive() || !ok;

    // Sticky bar
    if (this.sticky) {
      const priceEl = this.sticky.querySelector('[data-sp-sticky-price]');
      const metaEl = this.sticky.querySelector('[data-sp-sticky-meta]');
      const btn = this.sticky.querySelector('[data-sp-sticky-btn]');
      if (priceEl) priceEl.textContent = v ? formatMoney(total, fmt) : '';
      const optText = this.mixActive() ? 'mixed sizes' : options.filter((o, i) => this.data.variants.some((x) => x.options[i] !== o)).join(' / ');
      if (metaEl) metaEl.textContent = [`${qty} ${this.unitWord(qty)}`, optText].filter(Boolean).join(' · ');
      if (btn) btn.textContent = ok ? this.data.buttonLabel : this.addLabel?.textContent || this.data.soldOutLabel;
    }

    if (fromUser && v) {
      if (this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (v.featured_media) this.goToMedia(v.featured_media.id, true);
    }
  }

  renderMix(qty, options) {
    if (!this.mixWrap || this.data.mixOptionIndex < 0) return;
    if (this.mixToggleWrap) this.mixToggleWrap.hidden = qty < 2;
    const active = this.mixActive();
    this.mixWrap.hidden = !active;
    // While choosing per-pair sizes, hide the single size picker to avoid two conflicting choices
    const sizeFieldset = this.querySelector(`[data-sp-option="${this.data.mixOptionIndex}"]:not([data-sp-fixed])`)?.closest('.sp-fieldset');
    if (sizeFieldset) sizeFieldset.hidden = active;
    if (!active) {
      this.mixWrap.replaceChildren();
      this.mixValues = [];
      return;
    }
    const idx = this.data.mixOptionIndex;
    const values = [...new Set(this.data.variants.map((v) => v.options[idx]))].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
    const mainSelect = this.querySelector(`select[data-sp-option="${idx}"]`);
    const labelFor = (val) => {
      const opt = mainSelect && [...mainSelect.options].find((o) => o.value === val);
      return (opt && opt.dataset.label) || val;
    };
    const optionName = this.data.options[idx];
    if (this.mixWrap.children.length === qty) return;

    this.mixWrap.replaceChildren();
    for (let i = 0; i < qty; i++) {
      if (!this.mixValues[i]) this.mixValues[i] = options[idx];
      const row = document.createElement('div');
      row.className = 'sp-mix__row';
      const id = `sp-mix-${this.dataset.sectionId}-${i}`;
      const select = document.createElement('select');
      select.id = id;
      select.dataset.spMixSelect = String(i);
      const label = document.createElement('label');
      label.htmlFor = id;
      label.textContent = `${this.data.unit.charAt(0).toUpperCase()}${this.data.unit.slice(1)} ${i + 1} ${optionName.toLowerCase()}`;
      values.forEach((val) => {
        const probe = options.slice();
        probe[idx] = val;
        const variant = this.findVariant(probe);
        const opt = document.createElement('option');
        opt.value = val;
        opt.textContent = variant && variant.available ? labelFor(val) : `${labelFor(val)} (sold out)`;
        opt.disabled = !(variant && variant.available);
        opt.selected = val === this.mixValues[i];
        select.append(opt);
      });
      row.append(label, select);
      this.mixWrap.append(row);
      enhanceSelect(select);
    }
    this.mixValues.length = qty;
  }

  mixItems(options) {
    const idx = this.data.mixOptionIndex;
    const counts = new Map();
    for (const val of this.mixValues) {
      const probe = options.slice();
      probe[idx] = val;
      const v = this.findVariant(probe);
      if (!v || !v.available) return null;
      counts.set(v.id, (counts.get(v.id) || 0) + 1);
    }
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  /* ---------- cart ---------- */
  setStatus(html, tone) {
    if (!this.status) return;
    this.status.dataset.tone = tone || '';
    this.status.innerHTML = html;
  }

  async onSubmit(e) {
    e.preventDefault();
    const options = this.selectedOptions();
    const v = this.variant;
    if (!v || !v.available) return;
    const qty = this.quantity();
    const items = this.mixActive() ? this.mixItems(options) : [{ id: v.id, quantity: qty }];
    if (!items) {
      this.setStatus('One of the selected sizes is sold out. Please choose another.', 'error');
      return;
    }

    this.addBtn.disabled = true;
    this.addBtn.setAttribute('aria-busy', 'true');
    this.setStatus('Adding to cart…');

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (_) {
      events = null;
    }
    const CartEvent = events && events.CartLinesUpdateEvent;
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartEvent && CartEvent.createPromise ? CartEvent.createPromise() : null;
    if (deferred) {
      this.dispatchEvent(
        new CartEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const body = await res.json();
      if (!res.ok || body.status) throw new Error(body.description || body.message || 'Could not add to cart.');

      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (deferred) {
        deferred.resolve({
          cart: CartEvent.createCartFromAjaxResponse ? CartEvent.createCartFromAjaxResponse(cart) : cart,
          detail: {
            items: cart.items,
            source: 'sp-product',
            sourceId: this.id || this.dataset.sectionId,
            itemCount: qty,
            productId: String(this.data.productId),
            sections: body.sections,
            didError: false,
          },
        });
      }

      const hasDrawer = !!document.querySelector('cart-drawer-component');
      if (this.dataset.afterAdd === 'cart' || !hasDrawer) {
        window.location.href = `${root()}cart`;
        return;
      }
      this.setStatus(
        `Added ${qty} ${this.unitWord(qty)} to your cart. <a href="${root()}cart">View cart</a>`,
        'ok'
      );
    } catch (err) {
      if (deferred && deferred.reject) deferred.reject(err);
      this.setStatus(err.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      this.addBtn.removeAttribute('aria-busy');
      this.update(false);
    }
  }

  /* ---------- gallery ---------- */
  initGallery() {
    this.stage = this.querySelector('[data-sp-stage]');
    if (!this.stage) return;
    const initial = this.stage.querySelector('[data-initial]');
    if (initial) this.goToMedia(initial.dataset.mediaId, false);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            entry.target.querySelector('video')?.pause();
            return;
          }
          const id = entry.target.dataset.mediaId;
          this.querySelectorAll('[data-sp-thumb]').forEach((t) =>
            t.setAttribute('aria-current', String(t.dataset.spThumb === id))
          );
        });
      },
      { root: this.stage, threshold: 0.6 }
    );
    this.stage.querySelectorAll('.sp-gallery__slide').forEach((s) => io.observe(s));
    (this._observers ||= []).push(io);
  }

  goToMedia(mediaId, smooth) {
    if (!this.stage) return;
    const slide = this.stage.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.stage.scrollTo({ left: slide.offsetLeft, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    this.querySelectorAll('[data-sp-thumb]').forEach((t) =>
      t.setAttribute('aria-current', String(t.dataset.spThumb === String(mediaId)))
    );
  }

  /* ---------- sticky bar ---------- */
  initSticky() {
    if (!this.sticky || !this.addBtn) return;
    const io = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.sticky.toggleAttribute('data-visible', past);
      this.sticky.setAttribute('aria-hidden', String(!past));
      this.sticky.inert = !past;
    });
    io.observe(this.addBtn);
    (this._observers ||= []).push(io);
  }
}
if (!customElements.get('sp-product')) customElements.define('sp-product', SpProduct);

/* ------------------------------------------------------------------ */
/* Video band: muted loop that only plays in view, with a pause button  */
/* ------------------------------------------------------------------ */
class SpVideo extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.video = this.querySelector('video');
    this.btn = this.querySelector('[data-sp-video-toggle]');
    if (!this.video) return;
    this.video.muted = true;
    this.video.playsInline = true;
    this.userPaused = reducedMotion() || this.dataset.autoplay !== 'true';
    this.btn?.addEventListener('click', () => {
      if (this.video.paused) {
        this.userPaused = false;
        this.video.play().catch(() => {});
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    });
    this.video.addEventListener('play', () => this.sync());
    this.video.addEventListener('pause', () => this.sync());
    this.io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.userPaused) this.video.play().catch(() => {});
      else if (!entry.isIntersecting) this.video.pause();
    }, { threshold: 0.35 });
    this.io.observe(this);
    this.sync();
  }
  disconnectedCallback() {
    this.io?.disconnect();
  }
  sync() {
    if (!this.btn) return;
    const playing = !this.video.paused;
    this.btn.setAttribute('aria-pressed', String(!playing));
    this.btn.querySelector('[data-sp-label]').textContent = playing ? 'Pause video' : 'Play video';
    this.btn.querySelector('[data-sp-icon-play]').hidden = playing;
    this.btn.querySelector('[data-sp-icon-pause]').hidden = !playing;
  }
}
if (!customElements.get('sp-video')) customElements.define('sp-video', SpVideo);

/* ------------------------------------------------------------------ */
/* Ticker pause button                                                 */
/* ------------------------------------------------------------------ */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sp-ticker-toggle]');
  if (!btn) return;
  const ticker = btn.closest('.sp-ticker');
  const paused = ticker.toggleAttribute('data-paused');
  btn.setAttribute('aria-pressed', String(paused));
  btn.setAttribute('aria-label', paused ? 'Play announcements' : 'Pause announcements');
});
